import { useEffect } from "react";
import { useQueryClient, type Query } from "@tanstack/react-query";
import { getSocket } from "@/lib/socketClient";
import { onResync } from "@/lib/socketRecovery";
import { mocksEnabled } from "@/portals/clinician/api/transport";
import { patientKeys } from "@/portals/patient/api/patientQueries";
import { useNotificationsStore, type NotificationType } from "@/stores/notificationsStore";

// A review notification (new item, a clinician's question, a decision) means
// that profile's records, medications and review queue changed.
const REVIEW_TYPES: ReadonlySet<NotificationType> = new Set([
  "REVIEW_ITEM_NEEDS_INPUT",
  "REVIEW_ITEM_QUESTION",
  "REVIEW_ITEM_RESOLVED",
  // Module 5: an upload couldn't be confirmed — a new identity item is waiting.
  "IDENTITY_UNCONFIRMED",
]);
// Something a caregiver did changed this patient's family & caregiver lists.
const FAMILY_TYPES: ReadonlySet<NotificationType> = new Set([
  "ACCESS_REQUEST_RECEIVED",
  "ACCESS_CATEGORY_REQUESTED",
  "CAREGIVER_INVITE_ACCEPTED",
  "CAREGIVER_INVITE_DECLINED",
]);

/** The database announces a review question created, changed or removed (backend alembic f2a8c6e4b1d3). */
export const REVIEW_CHANGED_EVENT = "review:changed";
/** A burst of question changes (e.g. bulk review) reloads the review queries once. */
const REVIEW_BURST_MS = 250;

const CLINICAL = new Set(["records", "medications", "review"]);
/** Records / medications / review queries of every profile (the notification may be about a dependent). */
const isClinical = (query: Query) =>
  query.queryKey[0] === "patient" && query.queryKey[1] === "profile" && CLINICAL.has(String(query.queryKey[3]));
/** The review list and badge of every profile. Only these two: over a slow
 *  database link, also reloading an open question, the processing check and
 *  the rest on every change held connections for nothing (2026-10-26). */
const LIVE_REVIEW = new Set(["list", "count"]);
const isReview = (query: Query) =>
  query.queryKey[0] === "patient" && query.queryKey[1] === "profile" && query.queryKey[3] === "review"
  && LIVE_REVIEW.has(String(query.queryKey[4]));

/**
 * Turns realtime signals into cache invalidation for the patient portal —
 * whatever is on screen refetches in place, everything else is marked stale
 * and refetches the next time it's shown. Watches the notifications store
 * (fed by the socket and by the list's own refetches), so every new
 * notification counts once, however it arrived. A question created or
 * changed by a background job (e.g. an upload's identity check, which runs
 * after the upload itself finished) reloads the review list and badge at once
 * (2026-10-26: one stayed hidden until another answer reloaded the list).
 * After a reconnect / server resync, events may have been missed: everything
 * is revalidated.
 */
export function usePatientLiveQueries(): void {
  const client = useQueryClient();

  useEffect(() => {
    const seen = new Set(useNotificationsStore.getState().notifications.map((n) => n.id));
    return useNotificationsStore.subscribe((state) => {
      let review = false;
      let family = false;
      for (const n of state.notifications) {
        if (seen.has(n.id)) continue;
        seen.add(n.id);
        if (REVIEW_TYPES.has(n.type)) review = true;
        if (FAMILY_TYPES.has(n.type)) family = true;
      }
      if (review) void client.invalidateQueries({ predicate: isClinical });
      if (family) void client.invalidateQueries({ queryKey: patientKeys.family });
    });
  }, [client]);

  useEffect(() => {
    const socket = getSocket();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onReviewChanged = () => {
      if (timer) return;
      timer = setTimeout(() => {
        timer = undefined;
        void client.invalidateQueries({ predicate: isReview });
      }, REVIEW_BURST_MS);
    };
    socket.on(REVIEW_CHANGED_EVENT, onReviewChanged);
    return () => {
      socket.off(REVIEW_CHANGED_EVENT, onReviewChanged);
      if (timer) clearTimeout(timer);
    };
  }, [client]);

  useEffect(() => {
    if (mocksEnabled()) return;
    return onResync(getSocket(), () => void client.invalidateQueries({ queryKey: patientKeys.all }));
  }, [client]);
}
